/**
 * MENU CLIC DROIT D'UN SALARIÉ — les adresses et la lecture de l'identité
 * d'accès, en calculs purs (testés sans navigateur : tests/menu-personne.test.ts).
 *
 * Deux identifiants à ne jamais confondre :
 *   · person.id, côté RH : la fiche, les compteurs, la validation ;
 *   · l'identifiant UniFi Access (external_identity UNIFI_ACCESS, ACTIVE),
 *     côté Accès : la fiche Access et les passages aux portes.
 *
 * Chaque adresse est celle qu'une page de Gestion produit déjà : aucune
 * n'est inventée ici.
 */

const segment = (id: string) => encodeURIComponent(id)

/** La fiche salarié (App.tsx, `personDetail` ; lien de rh/PeoplePage.tsx). */
export const cheminFiche = (personId: string): string => `/equipe/rh/salaries/${segment(personId)}`

/** Les compteurs d'un salarié (App.tsx, `counterDetail` ; lien de CountersPages.tsx). */
export const cheminCompteurs = (personId: string): string => `/equipe/temps/compteurs/${segment(personId)}`

/**
 * Le poste de validation, ouvert sur le salarié — le lien que le serveur
 * produit déjà (packages/core/src/hr/accounts-read-model.ts). La page ne lit
 * `?person=` qu'au montage : le menu masque l'entrée quand on y est.
 */
export const CHEMIN_VALIDATION = '/equipe/temps/validation'
export const cheminValidation = (personId: string): string =>
  `${CHEMIN_VALIDATION}?${new URLSearchParams({ person: personId }).toString()}`

/**
 * Le planning d'UNE personne : `?personne=`, un filtre EXACT sur person.id
 * (PlanningPage.tsx, `personFilter`), jamais sur le nom — `?q=` reste la
 * recherche par sous-chaîne de la page. Lu au montage seulement, comme la
 * validation. Recopie de `planning-personne.ts` : ce modèle n'importe rien
 * (un test vérifie que les deux restent égaux).
 */
export const CHEMIN_PLANNING = '/equipe/temps/planning'
export const cheminPlanningPersonne = (personId: string): string =>
  `${CHEMIN_PLANNING}?${new URLSearchParams({ personne: personId }).toString()}`

/** L'identifiant qu'accepte la route de la fiche Access (App.tsx, `acPerson`). */
const ID_ACCES = /^[0-9a-f-]{8,40}$/

/** La fiche Access, comme les lignes de la liste Access (access/PeoplePage.tsx). */
export const cheminFicheAcces = (accesId: string): string => `/securite/controle-acces/personnes/${accesId}`

/**
 * Les passages d'une personne, comme « Voir tout l'historique » de la fiche
 * Access (access/PersonPage.tsx). L'historique ne lit son adresse qu'au
 * montage (AccessModule.tsx) : le menu masque l'entrée quand on y est.
 */
export const CHEMIN_PASSAGES = '/securite/controle-acces/historique'
export const cheminPassages = (accesId: string): string =>
  `${CHEMIN_PASSAGES}?personne=${segment(accesId)}&identifie=1`

export type IdentiteAcces = { ok: true; id: string } | { ok: false; message: string }

const objet = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null && !Array.isArray(x)

/**
 * Lit la réponse de GET /api/v1/rh/people/:id (apps/api/src/routes/rh.ts) :
 * l'identité UniFi Access ACTIVE, exactement comme la fiche salarié
 * (rh/PersonPage.tsx, `unifi`). Le refus du serveur (droit, périmètre) est
 * rendu tel quel : le menu ne contourne rien.
 */
export function lireIdentiteAcces(status: number, corps: unknown): IdentiteAcces {
  const o: Record<string, unknown> = objet(corps) ? corps : {}
  const message = typeof o['message'] === 'string' && o['message'] ? o['message'] : null
  if (status === 403) return { ok: false, message: message ?? 'Ce salarié est hors de votre périmètre.' }
  if (status === 404) return { ok: false, message: message ?? 'Salarié inconnu.' }
  if (status >= 400 || !Array.isArray(o['identities'])) return { ok: false, message: message ?? 'La fiche du salarié n’a pas pu être lue.' }
  const acces = (o['identities'] as unknown[]).find((i) => objet(i) && i['system'] === 'UNIFI_ACCESS' && i['status'] === 'ACTIVE')
  const id = objet(acces) && typeof acces['external_id'] === 'string' ? acces['external_id'] : null
  if (id === null) return { ok: false, message: 'Aucune identité d’accès active n’est liée à ce salarié.' }
  if (!ID_ACCES.test(id)) return { ok: false, message: 'L’identifiant d’accès de ce salarié ne correspond à aucune fiche Access.' }
  return { ok: true, id }
}

/**
 * La liste Salariés porte, ligne par ligne, le drapeau de l'API qui dit
 * qu'une identité de contrôle d'accès active est liée (rh.ts). Il est lu ici
 * pour que la page reste sans nom de fournisseur (§70).
 */
export function accesLieDeLigne(ligne: object): boolean | undefined {
  const v = (ligne as { unifi_linked?: unknown }).unifi_linked
  return typeof v === 'boolean' ? v : undefined
}
